/// <amd-module name="Test/TestPlansHubRefresh/TestCasesClonePanel/Redux/TestCasesClonePanelActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { TeamProjectReference, TeamProject } from "Tfs/Platform/Contracts/Core";
import { TestPlan, TestSuite } from "TestPlanCommon/Contracts/TestPlan";

export const TestCasesClonePanelActions = {
    /**
     * copyTestCases: copy the selected testcases into the destination test suite
     */
    copyTestCases: (
        sourcePlanId: number,
        sourcePlanName: string,
        destinationPlanId: number,
        destinationPlanName: string,
        sourceSuiteId: number,
        destinationSuiteId: number,
        sourceProjectName: string,
        destinationProjectName: string,
        testCaseIds: number[],
        includeLinks: boolean,
        includeAttachments: boolean
    ) =>
        createAction(TestCasesClonePanelActionsType.copyTestCases, {
            sourcePlanId,
            sourcePlanName,
            destinationPlanId,
            destinationPlanName,
            sourceSuiteId,
            destinationSuiteId,
            sourceProjectName,
            destinationProjectName,
            testCaseIds,
            includeLinks,
            includeAttachments
        }),

    /**
     * mandatoryFieldUpdated: this action is dispatched to update the flag for evaluating if all mandatory fields are updated.
     */
    mandatoryFieldUpdated: (isMandatoryFieldUpdated: boolean) =>
        createAction(TestCasesClonePanelActionsType.mandatoryFieldUpdated, isMandatoryFieldUpdated),

    /**
     * copyTestCasesCompleted: close the panel
     */
    copyTestCasesCompleted: (opId: number) => createAction(TestCasesClonePanelActionsType.copyTestCasesCompleted, { opId }),

    /**
     * copyTestCasesInProgress: invoke the reducer to show copying is in progress
     */
    copyTestCasesInProgress: () => createAction(TestCasesClonePanelActionsType.copyTestCasesInProgress),

    /**
     * getProjectsFromServer: fetch the projects via an async call
     */
    getProjectsFromServer: () => createAction(TestCasesClonePanelActionsType.getProjectsFromServer),

    /**
     * projectsFetchSucceeded: invoke the reducer to get the list of projects
     */
    projectsFetchSucceeded: (projectList: TeamProjectReference[]) => createAction(TestCasesClonePanelActionsType.projectsFetchSucceeded, projectList),

    /**
     * getTestPlans: fetch the list of test plans under the chosen project
     */
    getTestPlans: (projectName: string, continuationToken?: string) =>
        createAction(TestCasesClonePanelActionsType.getTestPlans, { projectName, continuationToken }),

    /**
     * testPlansFetchSucceeded: invoke the reducer to get the list of test plans under the chosen project
     */
    testPlansFetchSucceeded: (project: string, planList: TestPlan[]) =>
        createAction(TestCasesClonePanelActionsType.testPlansFetchSucceeded, { project, planList }),

    /**
     * getTestSuitesForPlan: fetch the list of test suites under the chosen test plan
     */
    getTestSuitesForPlan: (project: string, planId: number) => createAction(TestCasesClonePanelActionsType.getTestSuitesForPlan, { project, planId }),

    /**
     * getTestSuitesForPlanSucceeded: invoke the reducer to get the list of test suites under the chosen test plan
     */
    getTestSuitesForPlanSucceeded: (suiteList: TestSuite[]) => createAction(TestCasesClonePanelActionsType.getTestSuitesForPlanSucceeded, suiteList),

    /**
     * testCasesClonePanelError: invoke the reducer to render error while cloning test cases
     */
    testCasesClonePanelError: (testPlanSelectionErrorMessage: string) =>
        createAction(TestCasesClonePanelActionsType.testCasesClonePanelError, { testPlanSelectionErrorMessage }),

    /**
     * testCasesClonePanelSuiteSelectionError: invoke the reducer to render any error while selecting suite
     */
    testCasesClonePanelSuiteSelectionError: (testSuiteSelectionErrorMessage: string) =>
        createAction(TestCasesClonePanelActionsType.testCasesClonePanelSuiteSelectionError, { testSuiteSelectionErrorMessage }),

    /**
     * disableTestSuiteEditableDropdownSelection: invoke the reducer to disable the suite selection dropdown
     */
    disableTestSuiteEditableDropdownSelection: (disableTestSuiteDropdown: boolean) =>
        createAction(TestCasesClonePanelActionsType.disableTestSuiteEditableDropdownSelection, disableTestSuiteDropdown)

};

export const enum TestCasesClonePanelActionsType {
    copyTestCases = "TestCasesClonePanel/copyTestCases",
    mandatoryFieldUpdated = "TestCasesClonePanel/mandatoryFieldUpdated",
    copyTestCasesInProgress = "TestCasesClonePanel/copyTestCasesInProgress",
    copyTestCasesCompleted = "TestCasesClonePanel/copyTestCasesCompleted",
    getProjectsFromServer = "TestCasesClonePanel/getProjectsFromServer",
    projectsFetchSucceeded = "TestCasesClonePanel/projectsFetchSucceeded",
    getTestPlans = "TestCasesClonePanel/getTestPlans",
    testPlansFetchSucceeded = "TestCasesClonePanel/testPlansFetchSucceeded",
    getTestSuitesForPlan = "TestCasesClonePanel/getTestSuitesForPlan",
    getTestSuitesForPlanSucceeded = "TestCasesClonePanel/getTestSuitesForPlanSucceeded",
    testCasesClonePanelError = "TestCasesClonePanel/testCasesClonePanelError",
    testCasesClonePanelSuiteSelectionError = "TestCasesClonePanel/testCasesClonePanelSuiteSelectionError",
    disableTestSuiteEditableDropdownSelection = "TestCasesClonePanel/disableTestSuiteEditableDropdownSelection",
}

export type TestCasesClonePanelActions = ActionsUnion<typeof TestCasesClonePanelActions>;
